import React, { memo, useCallback, useMemo, useState } from "react";
import { shallowEqual } from "react-redux";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import styles from "./Engine.module.scss";
import { selectMotorization } from "../../../../../redux/slices/CarConfigMotorizationSlice";
import { useCarConfigSelector } from "../../../../../redux/store";
import EngineItem from "./EngineItem";
import { CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import SectionWrapper, { type SectionWrapperPropagationProps } from "../SectionWrapper";
import GradeSwitch from "../../../common/header/GradeSwitch";
import BackButton from "../../../common/header/BackButton";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import Collapse from "../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import EngineSkeleton from "./EngineSkeleton";
import { scrollToTarget } from "../../../../../../../../common-deprecated/utils/scrollUtils";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import { getGrLogo } from "../../../../../../../../common-deprecated/utils";
import { GrCode } from "../../../../../../../../common-deprecated/constants";
import GRLogo from "../../../../../../../../../assets/toyota/svg/GR/TME-GR.svg";
import GRLogoSport from "../../../../../../../../../assets/toyota/svg/GR/TME-GR-sport-black.svg";
import Badge from "../../../../../../shared-components/css-modules/toyota/atoms/Badge";

// The amount of minimum engines that should be available before view all is added
const MIN_ENGINE_LENGTH = 4;

const Engine: React.FC<SectionWrapperPropagationProps> = ({ sectionRefs }) => {
    const modelName = useCarConfigSelector((state) => state.car.model.webtitleSectionName);
    const gradeName = useCarConfigSelector((state) => state.car.grade.webtitleSectionName);
    const motorizationIsInitialized = useCarConfigSelector((state) => state.carConfigMotorization.initialized);
    const motorizations = useCarConfigSelector(selectMotorization, shallowEqual);
    const motorizationsInOtherGrades = useCarConfigSelector(
        (state) => state.availableInOtherGrades.availableMotorizations,
    );

    const grLogo = getGrLogo(modelName, gradeName);

    const [carConfigBuyNow, ariaShowMoreEngines, ariaShowLessEngines] = useCarConfigLabel([
        "carConfigAvailableInOtherGradesSummaryTitle",
        "carConfigA11yButtonShowMoreEngines",
        "carConfigA11yButtonShowLessEngines",
    ]);
    // Initial value set by amount of engines
    const [hideEngines, setHideEngines] = useState<boolean>(motorizations.length > MIN_ENGINE_LENGTH);

    const toggleEngines = useCallback((): void => {
        setHideEngines(!hideEngines);
        if (!hideEngines) {
            scrollToTarget(CarConfigConfigureSection.Engine);
        }
    }, [hideEngines]);

    return useMemo(
        () => (
            <SectionWrapper
                sectionId={CarConfigConfigureSection.Engine}
                sectionRef={sectionRefs.get(CarConfigConfigureSection.Engine)}
            >
                <BackButton sizing="xs" />
                <div className={styles.gradeswitch}>
                    <GradeSwitch />
                </div>
                <div className={styles.startingSection}>
                    {grLogo && (
                        <Badge className={styles.grlogo}>
                            {grLogo === GrCode.Gr ? <GRLogo aria-hidden="true" /> : <GRLogoSport aria-hidden="true" />}
                            <CarConfigLabel label="carConfigGazooRacing" />
                        </Badge>
                    )}
                    <Text tag="h1" variant="apheleia-h1" spacing="spacing-4" className={styles["main-title"]}>
                        <CarConfigLabel
                            label="carConfigMainTitle"
                            params={{
                                model: `${modelName} <strong>${gradeName}</strong>`,
                            }}
                            useHTML
                        />
                    </Text>
                    <Text tag="h2" variant="apheleia-h2" spacing="spacing-4">
                        <CarConfigLabel label="carConfigEngine" />
                    </Text>
                    <div className={styles.overview}>
                        <EngineSkeleton loading={!motorizationIsInitialized}>
                            {motorizations
                                .slice(0, hideEngines ? MIN_ENGINE_LENGTH : motorizations.length)
                                .map((motorization) => (
                                    <EngineItem
                                        key={`${motorization.engine.id}${motorization.transmission.id}${motorization.wheeldrive.id}`}
                                        motorization={motorization}
                                    />
                                ))}
                        </EngineSkeleton>
                        {motorizations.length > MIN_ENGINE_LENGTH && (
                            <div className={styles.viewall}>
                                <Button
                                    variant="btn-toggle"
                                    onClick={toggleEngines} // Call toggleEngines directly
                                    aria-expanded={!hideEngines}
                                    aria-controls="engines-wrapper"
                                    aria-label={hideEngines ? ariaShowMoreEngines : ariaShowLessEngines}
                                >
                                    <CarConfigLabel
                                        label={hideEngines ? "carConfigEnginesShowAll" : "carConfigEnginesShowLess"}
                                    />
                                    <div className="btn-toggle-icon">
                                        <Icon variant={hideEngines ? "plus" : "minus"} color="primary-white" />
                                    </div>
                                </Button>
                            </div>
                        )}
                    </div>
                    {motorizationsInOtherGrades.length > 0 && (
                        <Collapse summary={carConfigBuyNow} className={styles.other}>
                            {/* Disable because this is only a prevention and not an actual click for the user*/}
                            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                            <div
                                onClick={(event) => {
                                    event.stopPropagation();
                                }}
                                className={styles.overview}
                            >
                                {motorizationsInOtherGrades.map((motorization) => (
                                    <EngineItem
                                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                        key={`${motorization.engine.id}${motorization.transmission.id}${motorization.wheeldrive.id}`}
                                        motorization={motorization}
                                        engineOtherGrade
                                    />
                                ))}
                            </div>
                        </Collapse>
                    )}
                </div>
            </SectionWrapper>
        ),
        [
            sectionRefs,
            modelName,
            gradeName,
            motorizationIsInitialized,
            motorizations,
            hideEngines,
            toggleEngines,
            motorizationsInOtherGrades,
        ],
    );
};

export default memo(Engine);
